import { useReducer, useCallback, useMemo } from 'react';
import type { ResearchProgressState, ResearchProgressPhase } from '../types/research';

// Phase definitions with display labels
const PHASE_LABELS: Record<string, string> = {
  idle: 'Idle',
  premise_validation: 'Validating premises',
  strategy_panel: 'Planning strategy',
  research: 'Searching regulations',
  research_wave_1: 'Searching regulations',
  gap_analysis: 'Analyzing coverage gaps',
  research_wave_2: 'Filling gaps',
  structuring: 'Building evidence structure',
  validation: 'Validating results',
  complete: 'Complete',
  // v3 pipeline phases
  discovery: 'Discovering sources',
  triage: 'Planning retrievals',
  retrieval: 'Retrieving content',
  investigation: 'Investigating leads',
  extraction: 'Extracting evidence',
};

// Ordered phase list for the timeline (v2 full pipeline)
const V2_PHASES = [
  'premise_validation',
  'strategy_panel',
  'research',
  'gap_analysis',
  'research_wave_2',
  'structuring',
  'validation',
];

// V1 phases (subset)
const V1_PHASES = [
  'strategy_panel',
  'research',
  'structuring',
  'validation',
];

// V3 pipeline phases (deterministic 7-phase)
const V3_PHASES = [
  'discovery',
  'triage',
  'retrieval',
  'investigation',
  'extraction',
  'structuring',
  'validation',
];

function getLabelForPhase(name: string): string {
  return PHASE_LABELS[name] || name;
}

export type Action =
  | { type: 'START'; trackingId: string; messageId?: string }
  | { type: 'RESET'; trackingId: string }
  | { type: 'RESET_ALL' }
  | { type: 'PHASE_CHANGE'; trackingId: string; payload: { old_phase: string; new_phase: string } }
  | { type: 'WAVE_START'; trackingId: string; payload: { wave: number; worker_count?: number; layers?: string[] } }
  | { type: 'FINDING'; trackingId: string; payload: { id: string; summary: string; source?: string } }
  | { type: 'COVERAGE_METRICS'; trackingId: string; payload: { layers_covered: string[]; layers_missing: string[]; gaps_count: number; findings_count: number } }
  | { type: 'GAP_ANALYSIS'; trackingId: string; payload: { gap_count?: number; gaps_count?: number; missing_layers?: string[]; layers_missing?: string[]; has_actionable_gaps?: boolean } }
  | { type: 'PREMISE_RESULT'; trackingId: string; payload: { corrections: unknown[]; total_premises: number } }
  | { type: 'DETAIL'; trackingId: string; payload: { message: string } }
  | { type: 'COMPLETE'; trackingId: string }
  | { type: 'RESTORE'; trackingId: string; messageId?: string; payload: ResearchProgressState };

const initialTaskState: ResearchProgressState = {
  isActive: false,
  currentPhase: 'idle',
  phases: [],
  waves: [],
  findings: [],
  coverage: null,
  gaps: null,
  premiseCorrections: 0,
  totalPremises: 0,
  startedAt: 0,
};

interface ResearchProgressMapState {
  tasks: Map<string, ResearchProgressState>;
  /** Reverse index: messageId → trackingId (for backward-compatible lookups) */
  messageIdIndex: Map<string, string>;
}

const initialState: ResearchProgressMapState = {
  tasks: new Map(),
  messageIdIndex: new Map(),
};

function buildPhases(phaseNames: string[], currentPhase: string): ResearchProgressPhase[] {
  let foundCurrent = false;
  return phaseNames.map((name) => {
    if (name === currentPhase) {
      foundCurrent = true;
      return { name, label: getLabelForPhase(name), status: 'active' as const, startedAt: Date.now() };
    }
    if (!foundCurrent) {
      return { name, label: getLabelForPhase(name), status: 'completed' as const };
    }
    return { name, label: getLabelForPhase(name), status: 'pending' as const };
  });
}

function detectPhaseSet(phases: ResearchProgressPhase[], newPhase: string): string[] {
  // If we already have phases, keep the same set
  if (phases.length > 0) {
    const existingNames = phases.map(p => p.name);
    // If new phase is in existing set, keep it
    if (existingNames.includes(newPhase)) return existingNames;
    // If it's a v2-only phase, switch to v2
    if (['premise_validation', 'gap_analysis', 'research_wave_2'].includes(newPhase)) return V2_PHASES;
    return existingNames;
  }
  // First phase change — detect pipeline version
  if (['premise_validation'].includes(newPhase)) return V2_PHASES;
  if (['discovery', 'triage', 'retrieval', 'investigation', 'extraction'].includes(newPhase)) return V3_PHASES;
  return V1_PHASES;
}

/** Reducer for a single research task */
function taskReducer(state: ResearchProgressState, action: Action): ResearchProgressState {
  switch (action.type) {
    case 'START':
      return {
        ...initialTaskState,
        trackingId: action.trackingId,
        messageId: action.messageId,
        isActive: true,
        startedAt: Date.now(),
      };

    case 'RESET':
      return initialTaskState;

    case 'PHASE_CHANGE': {
      const { new_phase } = action.payload;
      if (new_phase === 'complete') {
        return {
          ...state,
          isActive: false,
          currentPhase: 'complete',
          phases: state.phases.map(p => ({
            ...p,
            status: 'completed' as const,
          })),
        };
      }
      const phaseNames = detectPhaseSet(state.phases, new_phase);
      const normalizedPhase = new_phase === 'research_wave_1' ? 'research' : new_phase;
      return {
        ...state,
        currentPhase: normalizedPhase,
        phases: buildPhases(phaseNames, normalizedPhase),
      };
    }

    case 'WAVE_START':
      return {
        ...state,
        waves: [
          ...state.waves.map(w => ({ ...w, status: 'completed' as const })),
          {
            wave: action.payload.wave,
            workerCount: action.payload.worker_count || 0,
            layers: action.payload.layers || [],
            status: 'active' as const,
          },
        ],
      };

    case 'FINDING':
      return {
        ...state,
        findings: [
          ...state.findings,
          {
            id: action.payload.id || `f-${state.findings.length}`,
            summary: action.payload.summary,
            source: action.payload.source,
            timestamp: Date.now(),
          },
        ],
      };

    case 'DETAIL': {
      // Append detail message to the currently active phase
      const activeIdx = state.phases.findIndex(p => p.status === 'active');
      if (activeIdx < 0) return state;
      const updatedPhases = state.phases.map((p, i) => {
        if (i !== activeIdx) return p;
        const existing = p.details || [];
        // Keep last 5 details per phase to avoid bloat
        const updated = [...existing, action.payload.message].slice(-5);
        return { ...p, details: updated };
      });
      return { ...state, phases: updatedPhases };
    }

    case 'COVERAGE_METRICS':
      return {
        ...state,
        coverage: {
          layersCovered: action.payload.layers_covered,
          layersMissing: action.payload.layers_missing,
          gapsCount: action.payload.gaps_count,
          findingsCount: action.payload.findings_count,
        },
      };

    case 'GAP_ANALYSIS':
      return {
        ...state,
        gaps: {
          gapCount: action.payload.gap_count ?? action.payload.gaps_count ?? 0,
          missingLayers: action.payload.missing_layers ?? action.payload.layers_missing ?? [],
          hasActionableGaps: action.payload.has_actionable_gaps ?? false,
        },
      };

    case 'PREMISE_RESULT':
      return {
        ...state,
        premiseCorrections: action.payload.corrections?.length ?? 0,
        totalPremises: action.payload.total_premises,
      };

    case 'COMPLETE':
      return {
        ...state,
        isActive: false,
        currentPhase: 'complete',
        phases: state.phases.map(p => ({
          ...p,
          status: 'completed' as const,
        })),
      };

    case 'RESTORE':
      return action.payload;

    default:
      return state;
  }
}

/** Top-level reducer: routes actions to per-task state by trackingId */
function reducer(state: ResearchProgressMapState, action: Action): ResearchProgressMapState {
  if (action.type === 'RESET_ALL') {
    return { tasks: new Map(), messageIdIndex: new Map() };
  }

  // All other actions target a specific task by trackingId
  const trackingId = (action as { trackingId: string }).trackingId;
  if (!trackingId) return state;

  const currentTask = state.tasks.get(trackingId) || { ...initialTaskState, trackingId };
  const updatedTask = taskReducer(currentTask, action);

  const newTasks = new Map(state.tasks);
  const newIndex = new Map(state.messageIdIndex);

  if (action.type === 'RESET') {
    newTasks.delete(trackingId);
  } else {
    newTasks.set(trackingId, updatedTask);
  }

  // Build messageId → trackingId index for backward-compatible lookups
  if (action.type === 'START' || action.type === 'RESTORE') {
    const messageId = (action as { messageId?: string }).messageId;
    if (messageId) {
      newIndex.set(messageId, trackingId);
    }
  }

  return { tasks: newTasks, messageIdIndex: newIndex };
}

/**
 * Map the Redis-backed progress object from the server endpoint
 * to a full ResearchProgressState for the reducer.
 */
export function mapServerProgress(server: Record<string, unknown>, trackingId?: string): ResearchProgressState {
  const phases = (server.phases as Array<{ name: string; status: string }>) || [];
  const findings = (server.findings as Array<{ id: string; summary: string }>) || [];
  const startedAtRaw = server.startedAt as string | number | undefined;
  const startedAt = typeof startedAtRaw === 'number'
    ? startedAtRaw
    : typeof startedAtRaw === 'string'
      ? new Date(startedAtRaw).getTime()
      : Date.now();

  return {
    trackingId,
    isActive: !!server.isActive,
    currentPhase: (server.currentPhase as string) || 'idle',
    phases: phases.map(p => ({
      name: p.name,
      label: getLabelForPhase(p.name),
      status: (p.status === 'completed' ? 'completed' : p.status === 'active' ? 'active' : 'pending') as 'completed' | 'active' | 'pending',
    })),
    waves: [],
    findings: findings.map((f, i) => ({
      id: f.id || `f-${i}`,
      summary: f.summary || '',
      timestamp: startedAt + i * 1000,
    })),
    coverage: null,
    gaps: null,
    premiseCorrections: 0,
    totalPremises: 0,
    startedAt,
  };
}

export function useResearchProgress() {
  const [state, dispatch] = useReducer(reducer, initialState);

  /** Dual-key lookup: try trackingId first, then messageId → trackingId index */
  const getProgress = useCallback((key: string): ResearchProgressState | undefined => {
    // Direct lookup (key is a trackingId like "res_008cf009")
    const direct = state.tasks.get(key);
    if (direct) return direct;
    // Index lookup (key is a messageId, translate to trackingId)
    const trackingId = state.messageIdIndex.get(key);
    if (trackingId) return state.tasks.get(trackingId);
    return undefined;
  }, [state.tasks, state.messageIdIndex]);

  const hasActiveResearch = useMemo(() => {
    return Array.from(state.tasks.values()).some(task => task.isActive);
  }, [state.tasks]);

  const handleResearchEvent = useCallback((trackingId: string, eventType: string, data: Record<string, unknown>) => {
    switch (eventType) {
      case 'phase_change':
        dispatch({
          type: 'PHASE_CHANGE',
          trackingId,
          payload: {
            old_phase: (data.old_phase as string) || '',
            new_phase: (data.new_phase as string) || '',
          },
        });
        break;
      case 'research_wave_start':
        dispatch({
          type: 'WAVE_START',
          trackingId,
          payload: {
            wave: (data.wave as number) || 1,
            worker_count: data.worker_count as number,
            layers: data.layers as string[],
          },
        });
        break;
      case 'finding':
        if (data.summary || data.title) {
          dispatch({
            type: 'FINDING',
            trackingId,
            payload: {
              id: (data.finding_id as string) || (data.id as string) || '',
              summary: (data.summary as string) || (data.title as string) || '',
              source: data.source as string,
            },
          });
        }
        break;
      case 'coverage_metrics':
        dispatch({
          type: 'COVERAGE_METRICS',
          trackingId,
          payload: {
            layers_covered: (data.layers_covered as string[]) || [],
            layers_missing: (data.layers_missing as string[]) || [],
            gaps_count: (data.gaps_count as number) || 0,
            findings_count: (data.findings_count as number) || 0,
          },
        });
        break;
      case 'research_gap_analysis':
        dispatch({
          type: 'GAP_ANALYSIS',
          trackingId,
          payload: {
            gap_count: data.gap_count as number,
            gaps_count: data.gaps_count as number,
            missing_layers: data.missing_layers as string[],
            layers_missing: data.layers_missing as string[],
            has_actionable_gaps: data.has_actionable_gaps as boolean,
          },
        });
        break;
      case 'premise_result':
        dispatch({
          type: 'PREMISE_RESULT',
          trackingId,
          payload: {
            corrections: (data.corrections as unknown[]) || [],
            total_premises: (data.total_premises as number) || 0,
          },
        });
        break;
    }
  }, []);

  return { researchProgressMap: state.tasks, getProgress, hasActiveResearch, dispatch, handleResearchEvent };
}
